import { useIngestionPublicSummary } from "@/features/landing";
import { cn } from "@/lib/utils";

function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

interface ConsoleStatusRowProps {
  /** When true, renders the row only on md+ (used in the nav). */
  mdOnly?: boolean;
  className?: string;
  /** Optional left-side label override. */
  label?: string;
  /** Compact mode: no container width cap, tighter gaps (drawer usage). */
  compact?: boolean;
}

/**
 * Thin instrument-surface status strip used by the global Navigation and
 * Footer. Mirrors the v2 landing's ConsoleHeaderV2/FooterBuildStatusV2 look
 * so layout chrome feels like part of the operations console.
 *
 * Reuses TanStack Query's cache via a stable key so this query is shared
 * across nav, footer, and the landing console headers.
 */
export function ConsoleStatusRow({
  mdOnly = false,
  className,
  label = "Operations console · Live",
  compact = false,
}: ConsoleStatusRowProps) {
  const { data, isLoading } = useIngestionPublicSummary();

  const lastRunRelative = relativeTime(data?.last_successful_run_at);
  const sources = data?.active_source_count ?? null;

  return (
    <div
      role="status"
      aria-label="System status"
      className={cn(
        "console-strip border-b",
        mdOnly && "hidden md:block",
        className
      )}
    >
      <div
        className={cn(
          "flex items-center h-6 overflow-hidden whitespace-nowrap",
          compact ? "px-3 gap-x-3" : "container mx-auto px-4 gap-x-4",
        )}
      >
        <span className="flex items-center gap-2 text-[hsl(var(--surface-instrument-fg))]">
          <span
            aria-hidden="true"
            className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"
          />
          <span className="hidden sm:inline">{label}</span>
          <span className="sm:hidden">Live</span>
        </span>

        <span aria-hidden="true" className="text-[hsl(var(--surface-instrument-fg)/0.3)]">/</span>

        <span className="text-[hsl(var(--surface-instrument-fg)/0.7)]">
          {isLoading ? (
            <span className="inline-block h-2 w-10 rounded-sm bg-[hsl(var(--surface-instrument-fg)/0.15)] animate-pulse align-middle" />
          ) : sources != null ? (
            <>
              <span className="text-[hsl(var(--surface-instrument-fg))]">{sources}</span>{" "}
              sources
            </>
          ) : (
            <span className="opacity-60">— sources</span>
          )}
        </span>

        <span aria-hidden="true" className="hidden sm:inline text-[hsl(var(--surface-instrument-fg)/0.3)]">/</span>

        <span className="hidden sm:inline text-[hsl(var(--surface-instrument-fg)/0.7)]">
          {isLoading ? (
            <span className="inline-block h-2 w-14 rounded-sm bg-[hsl(var(--surface-instrument-fg)/0.15)] animate-pulse align-middle" />
          ) : lastRunRelative ? (
            <>
              ingest ·{" "}
              <span className="text-[hsl(var(--surface-instrument-fg))]">{lastRunRelative}</span>
            </>
          ) : (
            <span className="opacity-60">ingest · —</span>
          )}
        </span>

        <span className={cn("ml-auto text-[hsl(var(--surface-instrument-fg)/0.5)]", compact ? "hidden" : "hidden md:inline")}>
          rack_and_role
        </span>
      </div>
    </div>
  );
}
